import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/auth';
import axios from 'axios';
import { login as authLogin } from '../api/auth';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const TestAuth = () => {
  const [token, setToken] = useState('');
  const [testResult, setTestResult] = useState('');
  const [error, setError] = useState('');
  const [manualToken, setManualToken] = useState('');
  const [apiEndpoint, setApiEndpoint] = useState('/analytics/dashboard');
  
  // Login form state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginStatus, setLoginStatus] = useState('');

  // Group API endpoints by category
  const apiEndpoints = {
    test: [
      { value: '/test', label: 'API Server Test' },
      { value: '/analytics-test', label: 'Analytics Test' },
      { value: '/analytics-open/test', label: 'Open Analytics Test' },
    ],
    analytics: [
      { value: '/analytics/dashboard', label: 'Dashboard Analytics' },
      { value: '/analytics/sales-trend', label: 'Sales Trend' },
      { value: '/analytics/top-products', label: 'Top Products' },
      { value: '/analytics/simple-dashboard', label: 'Simple Dashboard' },
    ],
    expenses: [
      { value: '/expenses', label: 'All Expenses' },
      { value: '/expenses/summary-by-category', label: 'Expense Summary by Category' },
      { value: '/expenses/total-over-time', label: 'Total Expenses Over Time' },
      { value: '/expenses/categories', label: 'Expense Categories' },
    ],
    employees: [
      { value: '/employees', label: 'All Employees' },
      { value: '/employees/upcoming-shifts', label: 'Upcoming Shifts' },
    ],
    accounting: [
      { value: '/accounting/profit', label: 'Profit Calculation' },
      { value: '/accounting/product-profitability', label: 'Product Profitability' },
      { value: '/accounting/expense-breakdown', label: 'Expense Breakdown' },
      { value: '/accounting/cash-flow', label: 'Cash Flow' },
    ],
    cashCount: [
      { value: '/cash-counts', label: 'All Cash Counts' },
      { value: '/cash-counts/latest', label: 'Latest Cash Count' },
      { value: '/cash-counts/stats', label: 'Cash Count Stats' },
    ],
    invoices: [
      { value: '/invoices', label: 'All Invoices' },
      { value: '/invoices/overdue', label: 'Overdue Invoices' },
      { value: '/invoices/upcoming', label: 'Upcoming Invoices' },
    ],
    customers: [
      { value: '/customers', label: 'All Customers' },
      { value: '/customers/search?query=test', label: 'Search Customers (query=test)' },
    ]
  };

  useEffect(() => {
    // Log available localStorage keys
    console.log('All localStorage keys:');
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      console.log(`${key}: ${localStorage.getItem(key).substring(0, 20)}...`);
    }
    
    const storedToken = getToken();
    console.log('TestAuth - getToken returned:', storedToken ? `${storedToken.substring(0, 20)}... (${storedToken.length} chars)` : 'null');
    
    setToken(storedToken || 'No token found');
  }, []);

  const testAuthRequest = async () => {
    try {
      setError('');
      setTestResult('Testing...');
      
      console.log(`Making request to: ${API_URL}${apiEndpoint}`);
      console.log(`Using token: ${token.substring(0, 20)}...`);
      
      const response = await axios.get(`${API_URL}${apiEndpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setTestResult(JSON.stringify(response.data, null, 2));
    } catch (err) {
      setError(`Error: ${err.message}`);
      console.error('Test request failed:', err);
      
      if (err.response) {
        setTestResult(`Status: ${err.response.status}, Data: ${JSON.stringify(err.response.data)}`);
      }
    }
  };

  const testOpenEndpoint = async () => {
    try {
      setError('');
      setTestResult('Testing open endpoint...');
      
      const response = await axios.get(`${API_URL}/test`);
      setTestResult(JSON.stringify(response.data, null, 2));
    } catch (err) {
      setError(`Error: ${err.message}`);
      console.error('Open endpoint test failed:', err);
      
      if (err.response) {
        setTestResult(`Status: ${err.response.status}, Data: ${JSON.stringify(err.response.data)}`);
      }
    }
  };

  const saveManualToken = () => {
    localStorage.setItem('auth_token', manualToken);
    setToken(manualToken);
  };

  const clearToken = () => {
    localStorage.removeItem('auth_token');
    setToken('Token cleared');
  };

  const formatToken = () => {
    if (!token || token === 'No token found' || token === 'Token cleared') {
      return token;
    }

    try {
      // Check for proper token format first
      if (!token.includes('.')) {
        return `Invalid token format (no dots): ${token}`;
      }
      
      const parts = token.split('.');
      if (parts.length !== 3) {
        return `Invalid JWT format (expected 3 parts, got ${parts.length}): ${token}`;
      }

      try {
        const header = JSON.parse(atob(parts[0]));
        const payload = JSON.parse(atob(parts[1]));

        return (
          <div>
            <div>
              <strong>Header:</strong> {JSON.stringify(header, null, 2)}
            </div>
            <div>
              <strong>Payload:</strong> {JSON.stringify(payload, null, 2)}
            </div>
            <div>
              <strong>Raw token:</strong> {token}
            </div>
          </div>
        );
      } catch (decodeError) {
        return `Error decoding token parts: ${decodeError.message}, Raw token: ${token}`;
      }
    } catch (e) {
      return `Error parsing token: ${e.message}, Raw token: ${token}`;
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginStatus('Logging in...');
    try {
      const result = await authLogin(username, password);
      setLoginStatus('Login successful');
      console.log('Login result:', result);
      // Update token display
      const storedToken = getToken();
      setToken(storedToken || 'No token found');
    } catch (error) {
      console.error('Login error:', error);
      setLoginStatus(`Login failed: ${error.message || 'Unknown error'}`);
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1>Authentication Test Page</h1>
      
      <div style={{ marginBottom: '20px' }}>
        <h2>Login Form</h2>
        <form onSubmit={handleLogin} style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '10px',
          marginBottom: '20px',
          padding: '15px',
          border: '1px solid #ddd',
          borderRadius: '5px'
        }}>
          <div>
            <label htmlFor="username" style={{ display: 'block', marginBottom: '5px' }}>Username:</label>
            <input 
              type="text" 
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={{ padding: '8px', width: '100%' }}
            />
          </div>
          <div>
            <label htmlFor="password" style={{ display: 'block', marginBottom: '5px' }}>Password:</label>
            <input 
              type="password" 
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ padding: '8px', width: '100%' }}
            />
          </div>
          <button 
            type="submit"
            style={{ 
              padding: '8px 16px', 
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Login
          </button>
          {loginStatus && (
            <div style={{ 
              marginTop: '10px', 
              padding: '8px', 
              backgroundColor: loginStatus.includes('failed') ? '#ffebee' : '#e8f5e9',
              borderRadius: '4px'
            }}>
              {loginStatus}
            </div>
          )}
        </form>
      </div>
      
      <div style={{ marginBottom: '20px' }}>
        <h2>Current Token</h2>
        <pre 
          style={{ 
            backgroundColor: '#f4f4f4', 
            padding: '10px', 
            borderRadius: '5px',
            overflow: 'auto',
            maxHeight: '200px'
          }}
        >
          {formatToken()}
        </pre>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <div style={{ marginBottom: '10px' }}>
          <label htmlFor="apiEndpoint">API Endpoint: </label>
          <select 
            id="apiEndpoint"
            value={apiEndpoint} 
            onChange={(e) => setApiEndpoint(e.target.value)}
            style={{ 
              padding: '8px', 
              borderRadius: '4px',
              border: '1px solid #ddd',
              marginLeft: '10px',
              minWidth: '250px'
            }}
          >
            <optgroup label="Test Endpoints">
              {apiEndpoints.test.map(endpoint => (
                <option key={endpoint.value} value={endpoint.value}>{endpoint.label}</option>
              ))}
            </optgroup>
            <optgroup label="Analytics">
              {apiEndpoints.analytics.map(endpoint => (
                <option key={endpoint.value} value={endpoint.value}>{endpoint.label}</option>
              ))}
            </optgroup>
            <optgroup label="Expenses">
              {apiEndpoints.expenses.map(endpoint => (
                <option key={endpoint.value} value={endpoint.value}>{endpoint.label}</option>
              ))}
            </optgroup>
            <optgroup label="Employees">
              {apiEndpoints.employees.map(endpoint => (
                <option key={endpoint.value} value={endpoint.value}>{endpoint.label}</option>
              ))}
            </optgroup>
            <optgroup label="Accounting">
              {apiEndpoints.accounting.map(endpoint => (
                <option key={endpoint.value} value={endpoint.value}>{endpoint.label}</option>
              ))}
            </optgroup>
            <optgroup label="Cash Counts">
              {apiEndpoints.cashCount.map(endpoint => (
                <option key={endpoint.value} value={endpoint.value}>{endpoint.label}</option>
              ))}
            </optgroup>
            <optgroup label="Invoices">
              {apiEndpoints.invoices.map(endpoint => (
                <option key={endpoint.value} value={endpoint.value}>{endpoint.label}</option>
              ))}
            </optgroup>
            <optgroup label="Customers">
              {apiEndpoints.customers.map(endpoint => (
                <option key={endpoint.value} value={endpoint.value}>{endpoint.label}</option>
              ))}
            </optgroup>
          </select>
        </div>
        <button 
          onClick={testAuthRequest}
          style={{ 
            padding: '8px 16px', 
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '10px'
          }}
        >
          Test Auth Request
        </button>
        <button 
          onClick={testOpenEndpoint}
          style={{ 
            padding: '8px 16px', 
            backgroundColor: '#ff9800',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Test Open Endpoint
        </button>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Manual Token Entry</h3>
        <input 
          type="text" 
          value={manualToken} 
          onChange={(e) => setManualToken(e.target.value)}
          style={{ 
            width: '100%', 
            padding: '8px', 
            marginBottom: '10px',
            borderRadius: '4px',
            border: '1px solid #ddd'
          }}
          placeholder="Paste token here"
        />
        <button 
          onClick={saveManualToken}
          style={{ 
            padding: '8px 16px', 
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginRight: '10px'
          }}
        >
          Save Token
        </button>
        <button 
          onClick={clearToken}
          style={{ 
            padding: '8px 16px', 
            backgroundColor: '#f44336',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Clear Token
        </button>
      </div>

      {error && (
        <div 
          style={{ 
            color: 'red', 
            backgroundColor: '#ffebee',
            padding: '10px',
            borderRadius: '4px',
            marginBottom: '20px'
          }}
        >
          {error}
        </div>
      )}

      {testResult && (
        <div style={{ marginBottom: '20px' }}>
          <h3>Test Result</h3>
          <pre 
            style={{ 
              backgroundColor: '#f4f4f4', 
              padding: '10px', 
              borderRadius: '5px',
              overflow: 'auto',
              maxHeight: '300px'
            }}
          >
            {testResult}
          </pre>
        </div>
      )}
    </div>
  );
};

export default TestAuth; 